import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { PDFData, getApgarTotals } from './utils/pdfDataLoader';
import { formatTimestampIL, formatTimestampShort } from './utils/sectionStartTimes';
import appLogo from '../../../assets/logo/IconOnly_Transparent_NoBuffer.png';

// Register Hebrew font
Font.register({
  family: 'Noto',
  fonts: [
    {
      src: '/fonts/noto/static/NotoSansHebrew-Regular.ttf',
      fontWeight: 400,
    },
    {
      src: '/fonts/noto/static/NotoSansHebrew-Bold.ttf',
      fontWeight: 700,
    },
  ]
});

/**
 * Renders text with proper directional handling for mixed Hebrew and English content.
 * Based on the solution from ChecklistPDF.jsx
 * Fixed to keep punctuation attached to adjacent Hebrew text for proper RTL display.
 */
const renderTextWithDirections = (text: string | number | null | undefined) => {
  if (!text && text !== 0) return <Text></Text>;

  const textStr = String(text);

  // Split text into segments: whitespace, numbers with decimals, punctuation, English, Hebrew
  const segments = textStr.split(/(\s+)|([0-9]+\.[0-9]+)|([,.\-_:;()'\"]+)|([a-zA-Z0-9][/a-zA-Z0-9\s]*)|([־\u0590-\u05FF]+)/g)
    .filter(segment => segment !== '' && segment !== undefined);

  const combinedSegments: Array<{
    text: string;
    isEnglish: boolean;
    isSpace: boolean;
    isPunctuation: boolean;
  }> = [];
  let currentEnglishSegment = '';

  segments.forEach((segment, index) => {
    // Handle whitespace
    if (/^\s+$/.test(segment)) {
      if (currentEnglishSegment) {
        combinedSegments.push({
          text: currentEnglishSegment.trim(),
          isEnglish: true,
          isSpace: false,
          isPunctuation: false
        });
        currentEnglishSegment = '';
      }

      combinedSegments.push({
        text: ' ',
        isEnglish: false,
        isSpace: true,
        isPunctuation: false
      });
      return;
    }

    // Handle punctuation - attach to previous Hebrew segment if exists
    if (/^[,.\-_:;()'\"]+$/.test(segment)) {
      if (currentEnglishSegment) {
        combinedSegments.push({
          text: currentEnglishSegment.trim(),
          isEnglish: true,
          isSpace: false,
          isPunctuation: false
        });
        currentEnglishSegment = '';
      }

      // Check if previous segment is Hebrew (not English, not space, not punctuation)
      const lastSegment = combinedSegments[combinedSegments.length - 1];
      if (lastSegment && !lastSegment.isEnglish && !lastSegment.isSpace && !lastSegment.isPunctuation) {
        // Attach punctuation to the previous Hebrew segment
        lastSegment.text = lastSegment.text + segment;
      } else {
        combinedSegments.push({
          text: segment,
          isEnglish: false,
          isSpace: false,
          isPunctuation: true
        });
      }
      return;
    }

    // Check if segment is English
    const isEnglish = /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/.test(segment);

    if (isEnglish) {
      currentEnglishSegment = currentEnglishSegment
        ? `${currentEnglishSegment}${segment}`
        : segment;
    } else {
      if (currentEnglishSegment) {
        combinedSegments.push({
          text: currentEnglishSegment.trim(),
          isEnglish: true,
          isSpace: false,
          isPunctuation: false
        });
        currentEnglishSegment = '';

        if (!combinedSegments[combinedSegments.length - 1]?.isSpace) {
          combinedSegments.push({
            text: ' ',
            isEnglish: false,
            isSpace: true,
            isPunctuation: false
          });
        }
      }

      combinedSegments.push({
        text: segment,
        isEnglish: false,
        isSpace: false,
        isPunctuation: false
      });
    }
  });

  // Handle remaining English segment
  if (currentEnglishSegment) {
    if (combinedSegments.length > 0 &&
        !combinedSegments[combinedSegments.length - 1].isEnglish &&
        !combinedSegments[combinedSegments.length - 1].isSpace) {
      combinedSegments.push({
        text: ' ',
        isEnglish: false,
        isSpace: true,
        isPunctuation: false
      });
    }

    combinedSegments.push({
      text: currentEnglishSegment.trim(),
      isEnglish: true,
      isSpace: false,
      isPunctuation: false
    });
  }

  // Remove duplicate spaces
  const cleanedSegments = combinedSegments.reduce<typeof combinedSegments>((acc, curr, idx, arr) => {
    if (curr.isSpace && arr[idx + 1]?.isSpace) {
      return acc;
    }
    acc.push(curr);
    return acc;
  }, []);

  // Render segments in RTL container
  return (
    <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
      {cleanedSegments.map((segment, index) => (
        <Text
          key={index}
          style={{
            fontFamily: 'Noto',
            color: '#1D426E',
            direction: segment.isEnglish ? 'ltr' : 'rtl',
          }}
        >
          {segment.text}
        </Text>
      ))}
    </View>
  );
};

// Styles - Optimized for better readability while fitting on one page
const styles = StyleSheet.create({
  page: {
    padding: 16,
    fontFamily: 'Noto',
    backgroundColor: 'white',
    color: '#1D426E',
    fontSize: 9,
  },
  header: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    minHeight: 28,
  },
  headerDate: {
    fontSize: 9,
    textAlign: 'right',
    color: '#1D426E',
  },
  headerCenter: {
    alignItems: 'center',
    flex: 1,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1D426E',
    textAlign: 'center',
  },
  motherName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1D426E',
    textAlign: 'center',
    marginTop: 3,
  },
  logo: {
    maxHeight: 28,
    objectFit: 'contain',
  },

  // Opening Conditions
  openingSection: {
    marginBottom: 8,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#1D426E',
  },
  openingGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
  },
  openingField: {
    width: '50%',
    marginBottom: 3,
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
  },
  fieldLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    marginLeft: 4,
    color: '#1D426E',
  },
  fieldValue: {
    fontSize: 9,
    color: '#1D426E',
  },

  // Checklist Sections
  section: {
    marginBottom: 6,
  },
  sectionHeader: {
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
    paddingBottom: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#1D426E',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1D426E',
    textAlign: 'right',
  },
  sectionStartTime: {
    fontSize: 8,
    color: '#666',
    textAlign: 'left',
  },
  itemsGrid: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  itemColumn: {
    width: '48%',
  },
  item: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  checkbox: {
    width: 9,
    height: 9,
    borderWidth: 1,
    borderColor: '#1D426E',
    marginLeft: 4,
    marginTop: 1,
    backgroundColor: 'white',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  checkboxChecked: {
    backgroundColor: '#1D426E',
  },
  checkmark: {
    fontSize: 6,
    color: 'white',
    fontWeight: 'bold',
  },
  itemLabel: {
    fontSize: 9,
    textAlign: 'right',
    color: '#1D426E',
    flex: 1,
  },

  // Apgar Section
  apgarSection: {
    marginTop: 8,
    marginBottom: 6,
  },
  apgarTimerInfo: {
    fontSize: 8,
    color: '#666',
    textAlign: 'right',
    marginBottom: 3,
  },
  apgarTable: {
    borderWidth: 0.5,
    borderColor: '#1D426E',
  },
  apgarRow: {
    flexDirection: 'row-reverse',
    borderBottomWidth: 0.5,
    borderBottomColor: '#1D426E',
    minHeight: 16,
  },
  apgarHeaderRow: {
    backgroundColor: '#f0f0f0',
  },
  apgarCriteriaCell: {
    width: '40%',
    padding: 3,
    fontSize: 8,
    textAlign: 'right',
    color: '#1D426E',
    borderLeftWidth: 0.5,
    borderLeftColor: '#1D426E',
    justifyContent: 'center',
  },
  apgarScoreCell: {
    width: '20%',
    padding: 3,
    fontSize: 8,
    textAlign: 'center',
    color: '#1D426E',
    borderLeftWidth: 0.5,
    borderLeftColor: '#1D426E',
    justifyContent: 'center',
  },
  apgarHeaderCell: {
    fontWeight: 'bold',
    fontSize: 9,
  },
  apgarTotalRow: {
    backgroundColor: '#e8f4f8',
    fontWeight: 'bold',
  },

  text: {
    fontFamily: 'Noto',
    textAlign: 'right',
    color: '#1D426E',
  },
});

interface NewPreemiePDFProps {
  data: PDFData;
}

const NewPreemiePDFDocument: React.FC<NewPreemiePDFProps> = ({ data }) => {
  const apgarTotals = getApgarTotals(data.apgar.scores);

  const formatCreationDate = (date: Date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year}  ${hours}:${minutes}`;
  };

  const renderChecklistSection = (
    title: string,
    section: typeof data.sections.medications
  ) => {
    if (section.items.length === 0) return null;

    // Split items into two columns
    const midpoint = Math.ceil(section.items.length / 2);
    const rightColumn = section.items.slice(0, midpoint);
    const leftColumn = section.items.slice(midpoint);

    return (
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <View style={styles.sectionTitle}>{renderTextWithDirections(title)}</View>
          {section.startTime && (
            <Text style={styles.sectionStartTime}>
              התחלה: {formatTimestampShort(section.startTime)}
            </Text>
          )}
        </View>
        <View style={styles.itemsGrid}>
          {/* Right column */}
          <View style={styles.itemColumn}>
            {rightColumn.map((item) => (
              <View key={item.key} style={styles.item}>
                <View style={[styles.checkbox, item.checked && styles.checkboxChecked]}>
                  {item.checked && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <View style={styles.itemLabel}>{renderTextWithDirections(item.label)}</View>
              </View>
            ))}
          </View>
          {/* Left column */}
          <View style={styles.itemColumn}>
            {leftColumn.map((item) => (
              <View key={item.key} style={styles.item}>
                <View style={[styles.checkbox, item.checked && styles.checkboxChecked]}>
                  {item.checked && <Text style={styles.checkmark}>✓</Text>}
                </View>
                <View style={styles.itemLabel}>{renderTextWithDirections(item.label)}</View>
              </View>
            ))}
          </View>
        </View>
      </View>
    );
  };

  const renderApgarTable = () => {
    const criteria = [
      { key: 'pulse', label: 'דופק' },
      { key: 'breathing', label: 'נשימה' },
      { key: 'muscleTone', label: 'טונוס שרירים' },
      { key: 'skinColor', label: 'צבע עור' },
      { key: 'touchResponse', label: 'תגובה למגע' }
    ];

    type CriteriaKey = 'pulse' | 'breathing' | 'muscleTone' | 'skinColor' | 'touchResponse';

    return (
      <View style={styles.apgarTable}>
        {/* Header Row */}
        <View style={[styles.apgarRow, styles.apgarHeaderRow]}>
          <View style={[styles.apgarCriteriaCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>קריטריון</Text>
          </View>
          <View style={[styles.apgarScoreCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>דקה</Text>
          </View>
          <View style={[styles.apgarScoreCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>5 דק'</Text>
          </View>
          <View style={[styles.apgarScoreCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>10 דק'</Text>
          </View>
        </View>

        {/* Criteria Rows */}
        {criteria.map((criterion) => (
          <View key={criterion.key} style={styles.apgarRow}>
            <View style={styles.apgarCriteriaCell}>
              <Text style={styles.text}>{criterion.label}</Text>
            </View>
            <View style={styles.apgarScoreCell}>
              <Text style={styles.text}>
                {data.apgar.scores[criterion.key as CriteriaKey]?.min1 ?? '-'}
              </Text>
            </View>
            <View style={styles.apgarScoreCell}>
              <Text style={styles.text}>
                {data.apgar.scores[criterion.key as CriteriaKey]?.min5 ?? '-'}
              </Text>
            </View>
            <View style={styles.apgarScoreCell}>
              <Text style={styles.text}>
                {data.apgar.scores[criterion.key as CriteriaKey]?.min10 ?? '-'}
              </Text>
            </View>
          </View>
        ))}

        {/* Total Row */}
        <View style={[styles.apgarRow, styles.apgarTotalRow]}>
          <View style={[styles.apgarCriteriaCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>סה"כ</Text>
          </View>
          <View style={[styles.apgarScoreCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>{apgarTotals.min1 ?? '-'}</Text>
          </View>
          <View style={[styles.apgarScoreCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>{apgarTotals.min5 ?? '-'}</Text>
          </View>
          <View style={[styles.apgarScoreCell, styles.apgarHeaderCell]}>
            <Text style={styles.text}>{apgarTotals.min10 ?? '-'}</Text>
          </View>
        </View>
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerDate}>{formatCreationDate(data.creationDate)}</Text>
          <View style={styles.headerCenter}>
            <View style={styles.headerTitle}>{renderTextWithDirections('קבלת פג ליחידה')}</View>
            <View style={styles.motherName}>{renderTextWithDirections(data.motherName)}</View>
          </View>
          <Image src={appLogo} style={styles.logo} />
        </View>

        {/* Opening Conditions */}
        <View style={styles.openingSection}>
          <View style={styles.sectionTitle}>{renderTextWithDirections('נתוני פתיחה')}</View>
          <View style={styles.openingGrid}>
            <View style={styles.openingField}>
              <Text style={styles.fieldLabel}>{':שבוע הריון'} </Text>
              <Text style={styles.fieldValue}>
                {data.openingConditions.gestationalAgeWeeks !== null && data.openingConditions.gestationalAgeWeeks !== undefined
                  ? `${data.openingConditions.gestationalAgeWeeks}+${data.openingConditions.gestationalAgeDays ?? 0}`
                  : 'לא הוזן'}
              </Text>
            </View>
            <View style={styles.openingField}>
              <Text style={styles.fieldLabel}>{':הערכת משקל'} </Text>
              <Text style={styles.fieldValue}>
                {data.openingConditions.weightAssessment != null
                  ? `${data.openingConditions.weightAssessment} גרם`
                  : 'לא הוזן'}
              </Text>
            </View>
            <View style={styles.openingField}>
              <Text style={styles.fieldLabel}>{':משקל לידה'} </Text>
              <Text style={styles.fieldValue}>
                {data.openingConditions.birthWeight != null
                  ? `${data.openingConditions.birthWeight} גרם`
                  : 'לא הוזן'}
              </Text>
            </View>
            <View style={styles.openingField}>
              <Text style={styles.fieldLabel}>{':מין'} </Text>
              <Text style={styles.fieldValue}>
                {data.openingConditions.gender || 'לא הוזן'}
              </Text>
            </View>
            <View style={styles.openingField}>
              <Text style={styles.fieldLabel}>{':שעת קבלה למחלקה'} </Text>
              <Text style={styles.fieldValue}>
                {data.openingConditions.admissionTime || 'לא הוזן'}
              </Text>
            </View>
            {data.openingConditions.birthReason && (
              <View style={[styles.openingField, { width: '100%' }]}>
                <Text style={styles.fieldLabel}>{':סיבה ללידה מוקדמת'} </Text>
                <Text style={styles.fieldValue}>
                  {data.openingConditions.birthReason}
                </Text>
              </View>
            )}
            {data.openingConditions.anamnesis && (
              <View style={[styles.openingField, { width: '100%' }]}>
                <Text style={styles.fieldLabel}>{':אנמנזה'} </Text>
                <Text style={styles.fieldValue}>
                  {data.openingConditions.anamnesis.substring(0, 300)}
                  {data.openingConditions.anamnesis.length > 300 && '...'}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Medications */}
        {renderChecklistSection('תרופות', data.sections.medications)}

        {/* Apgar Section */}
        <View style={styles.apgarSection}>
          <View style={styles.sectionTitle}>{renderTextWithDirections('שעון אפגר')}</View>
          {data.apgar.timerStartTime && (
            <Text style={styles.apgarTimerInfo}>
              {':טיימר התחיל'} {formatTimestampShort(data.apgar.timerStartTime)}
            </Text>
          )}
          {renderApgarTable()}
        </View>

        {/* Umbilical Cord Section */}
        {(() => {
          const ucOption = data.umbilicalCord.option;
          if (ucOption === null) return null;
          const OPTION_LABELS: Record<string, string> = {
            one_minute: 'דקה',
            less_than_minute: 'פחות מדקה',
            not_performed: 'לא בוצע',
          };
          const label = OPTION_LABELS[ucOption] || '';
          const reasonText = ucOption === 'not_performed' && data.umbilicalCord.reason
            ? ` - סיבה: ${data.umbilicalCord.reason}`
            : '';
          return (
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <View style={styles.sectionTitle}>{renderTextWithDirections('חיתוך מושהה של חבל הטבור')}</View>
              </View>
              <View style={{ padding: '4px 0' }}>
                <Text style={styles.text}>{label}{reasonText}</Text>
              </View>
            </View>
          );
        })()}

        {/* Department Admission */}
        {renderChecklistSection('קבלה למחלקה', data.sections.departmentAdmission)}

        {/* Parents */}
        {renderChecklistSection('עדכון הורים', data.sections.parents)}
      </Page>
    </Document>
  );
};

export default NewPreemiePDFDocument;
